import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Send email using the backend API
 * @param {Object} emailData - Email data object
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.body - Email body (HTML string)
 * @param {string} emailData.to - Recipient email (defaults to inspection@snageasy.in)
 * @param {string} emailData.from - Sender email (defaults to inspection@snageasy.in)
 * @param {string|Array} emailData.cc - CC email(s) (optional)
 * @param {string|Array} emailData.bcc - BCC email(s) (optional)
 * @returns {Promise} Promise that resolves with the API response
 */
export const sendEmail = async ({ subject, body, to, from, cc, bcc }) => {
  try {
    console.log('📧 sendEmail function called with:', { to, from, subject, hasBcc: !!bcc });
    
    // Ensure 'to' is provided and not empty
    if (!to || !to.trim()) {
      console.error('❌ ERROR: Recipient email address (to) is missing or empty!');
      throw new Error('Recipient email address (to) is required');
    }

    const trimmedTo = to.trim();
    console.log('📧 Trimmed recipient email:', trimmedTo);

    const emailData = {
      subject,
      body,
      to: trimmedTo, // Ensure no whitespace - THIS IS THE USER'S EMAIL
      from: from || 'inspection@snageasy.in',
    };

    // Only add cc/bcc if provided
    if (cc) {
      emailData.cc = cc;
    }
    if (bcc) {
      emailData.bcc = bcc;
    }

    console.log('📧 Final email data being sent to API:', { 
      to: emailData.to, 
      from: emailData.from, 
      subject: emailData.subject,
      hasBcc: !!emailData.bcc,
      bcc: emailData.bcc
    });
    console.log('📧 API URL:', `${API_BASE_URL}/api/send-email`);

    const response = await axios.post(`${API_BASE_URL}/api/send-email`, emailData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Email API response:', response.data);
    console.log('✅ Email sent to:', response.data.sentTo || emailData.to);
    
    if (response.data.sentTo && response.data.sentTo !== trimmedTo) {
      console.error('❌ MISMATCH: Requested to send to:', trimmedTo);
      console.error('❌ But API says it was sent to:', response.data.sentTo);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error sending email:', error);
    if (error.response) {
      console.error('Response error:', error.response.data);
    }
    throw error;
  }
};

/**
 * Format form data as HTML email body
 * @param {Object} formData - Form data object
 * @param {string} formType - Type of form (e.g., 'callback', 'booking')
 * @returns {string} HTML formatted email body
 */
export const formatEmailBody = (formData, formType = 'callback') => {
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #3F51B5 0%, #F47436 100%); color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9f9f9; padding: 20px; border-radius: 0 0 8px 8px; }
        .field { margin-bottom: 15px; }
        .label { font-weight: bold; color: #3F51B5; }
        .value { margin-top: 5px; padding: 8px; background: white; border-left: 3px solid #3F51B5; }
        .footer { margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; text-align: center; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h2>New ${formType === 'booking' ? 'Booking Request' : 'Callback Request'}</h2>
        </div>
        <div class="content">
  `;

  // Add form fields
  Object.keys(formData).forEach(key => {
    if (formData[key] && formData[key] !== '') {
      const label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      
      html += `
        <div class="field">
          <div class="label">${label}:</div>
          <div class="value">${formData[key]}</div>
        </div>
      `;
    }
  });

  html += `
        </div>
        <div class="footer">
          <p>This email was sent from the SnagEasy website contact form.</p>
          <p>Please do not reply to this email.</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};

